import { useState, useEffect, useRef, useCallback } from "react";
import { X, ChevronUp } from "lucide-react";
import { BACKEND_URL } from "@/config/runtime";
import { BannerArticleDrawer } from "./BannerArticleDrawer";
import { BannerPdfDrawer } from "./BannerPdfDrawer";

const COLLAPSE_KEY = "homepage_banner_collapsed"; // session-only

const trackImpression = (id) => {
  fetch(`${BACKEND_URL}/api/banners/${id}/impression`, { method: "POST" }).catch(() => {});
};
const trackClick = (id) => {
  fetch(`${BACKEND_URL}/api/banners/${id}/click`, { method: "POST" }).catch(() => {});
};

const useBannerSettings = () => {
  // Pulled from site_content via the SiteContent context that already loads it.
  // We re-read live values via window-attached siteContent for simplicity, or default.
  // Reads keys: banner_enabled, banner_rotation_transition, banner_rotation_interval
  return null; // settings injected per-call below
};

const BannerMedia = ({ banner, isMobile, onEnded }) => {
  const url = isMobile && banner.media_url_mobile ? banner.media_url_mobile : banner.media_url;
  const type = banner.media_type;
  const fit = banner.media_fit === "contain" ? "object-contain" : "object-cover";
  const objectPosition = `${banner.focal_x ?? 50}% ${banner.focal_y ?? 50}%`;
  if (type === "video") {
    return (
      <video
        src={url}
        autoPlay
        muted
        loop={!onEnded}
        playsInline
        onEnded={onEnded}
        className={`w-full h-full ${fit}`}
        style={{ objectPosition }}
        aria-label={banner.alt_text || ""}
        data-testid={`banner-media-${banner.banner_id}`}
      />
    );
  }
  return (
    <img
      src={url}
      alt={banner.alt_text || ""}
      className={`w-full h-full ${fit}`}
      style={{ objectPosition }}
      loading="eager"
      decoding="async"
      data-testid={`banner-media-${banner.banner_id}`}
    />
  );
};

/**
 * Homepage banner — rotates between active banners with admin-configured
 * transition + interval. Session-collapsible (with re-open tab). Tracks
 * impressions per view, clicks per click.
 */
export function HomepageBanner({ onOpenListing, siteContent, page = "homepage", categoryId = null }) {
  const [banners, setBanners] = useState([]);
  const [index, setIndex] = useState(0);
  const [collapsed, setCollapsed] = useState(() => {
    return sessionStorage.getItem(COLLAPSE_KEY) === "1";
  });
  const [articleOpen, setArticleOpen] = useState(false);
  const [activeArticleBanner, setActiveArticleBanner] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [activePdfBanner, setActivePdfBanner] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  // Per-banner activation counter — bumped each time a banner becomes active,
  // used as a key suffix so videos/GIFs remount and restart from frame 0.
  const [activations, setActivations] = useState({});
  const seenIds = useRef(new Set());

  const enabled = (siteContent?.banner_enabled || "false") === "true";
  const transition = siteContent?.banner_rotation_transition || "fade";
  const intervalSec = parseInt(siteContent?.banner_rotation_interval || "6", 10);
  // "timed" = use intervalSec for every banner (legacy)
  // "media_aware" = videos/GIFs play to their natural end, only photos use intervalSec
  const rotationMode = siteContent?.banner_rotation_mode || "timed";
  const gifPlaySec = parseInt(siteContent?.banner_gif_play_seconds || "10", 10);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Fetch banners on mount (and when enabled flips on, page/category changes)
  useEffect(() => {
    if (!enabled) return;
    const params = new URLSearchParams({ page });
    if (categoryId) params.set("category_id", categoryId);
    fetch(`${BACKEND_URL}/api/banners?${params.toString()}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        setBanners(Array.isArray(data) ? data : []);
        setIndex(0); // reset rotation when the banner set changes
      })
      .catch(() => setBanners([]));
  }, [enabled, page, categoryId]);

  const advance = useCallback(() => {
    setIndex((i) => (i + 1) % Math.max(1, banners.length));
  }, [banners.length]);

  // Rotation — driven either by a fixed interval (timed) or by the media type of
  // the currently-active banner (media_aware).
  useEffect(() => {
    if (banners.length <= 1 || collapsed) return;
    const current = banners[index];
    if (!current) return;

    // In media_aware mode, videos handle their own advance via the onEnded
    // listener attached to the <video> element below — skip the timer here.
    if (rotationMode === "media_aware" && current.media_type === "video") {
      return;
    }

    let ms;
    if (rotationMode === "media_aware" && current.media_type === "gif") {
      ms = Math.max(2000, gifPlaySec * 1000);
    } else {
      // photos, or any banner in "timed" mode
      ms = Math.max(2000, intervalSec * 1000);
    }
    const t = setTimeout(advance, ms);
    return () => clearTimeout(t);
  }, [banners, index, collapsed, rotationMode, intervalSec, gifPlaySec, advance]);

  // Track impression once per banner per session, and bump activation counter
  // so the freshly-active media (gif/video) restarts from the beginning.
  useEffect(() => {
    if (!banners.length || collapsed) return;
    const current = banners[index];
    if (!current) return;
    if (!seenIds.current.has(current.banner_id)) {
      seenIds.current.add(current.banner_id);
      trackImpression(current.banner_id);
    }
    // Only bump if this banner has media that benefits from restarting
    if (current.media_type === "video" || current.media_type === "gif") {
      setActivations((prev) => ({
        ...prev,
        [current.banner_id]: (prev[current.banner_id] || 0) + 1,
      }));
    }
  }, [banners, index, collapsed]);

  const handleCollapse = useCallback(() => {
    setCollapsed(true);
    sessionStorage.setItem(COLLAPSE_KEY, "1");
  }, []);

  const handleExpand = useCallback(() => {
    setCollapsed(false);
    sessionStorage.removeItem(COLLAPSE_KEY);
  }, []);

  const handleClick = useCallback((banner) => {
    trackClick(banner.banner_id);
    if (banner.click_action === "external_url" && banner.click_url) {
      window.open(banner.click_url, "_blank", "noopener,noreferrer");
    } else if (banner.click_action === "listing" && banner.click_listing_id) {
      // Resolve listing_id → slug then call the page's drawer opener
      fetch(`${BACKEND_URL}/api/listings/by-id/${banner.click_listing_id}`, { credentials: "include" })
        .then((r) => r.ok ? r.json() : null)
        .then((listing) => {
          if (listing?.slug && onOpenListing) {
            onOpenListing(listing.slug);
          }
        })
        .catch(() => {});
    } else if (banner.click_action === "article" && banner.click_article) {
      setActiveArticleBanner(banner);
      setArticleOpen(true);
    } else if (banner.click_action === "pdf_document" && banner.click_pdf_url) {
      setActivePdfBanner(banner);
      setPdfOpen(true);
    }
  }, [onOpenListing]);

  if (!enabled || banners.length === 0) return null;

  if (collapsed) {
    return (
      <button
        onClick={handleExpand}
        className="w-full bg-[#1E3A2F] text-white py-1.5 text-xs flex items-center justify-center gap-2 hover:bg-[#2D5A3D] transition-colors"
        data-testid="banner-expand-tab"
        aria-label="Show banner"
      >
        <ChevronUp className="w-3.5 h-3.5" />
        <span>Show featured banner</span>
      </button>
    );
  }

  const current = banners[index];
  if (!current) return null;

  // When fit is "contain", let the page background show through any empty space
  // around the image instead of a stone-coloured box.
  const wrapperBg = current.media_fit === "contain" ? "bg-transparent" : "bg-stone-100";

  return (
    <>
      {/* Outer container: side padding + breathing room on desktop only.
          Mobile is edge-to-edge as a slim strip. */}
      <div className="w-full sm:py-3 sm:px-4 lg:px-8">
        <div
          className={`relative ${wrapperBg} overflow-hidden w-full mx-auto sm:max-w-5xl sm:rounded-lg`}
          style={{
            aspectRatio: isMobile ? "6 / 1" : "12 / 1",
            maxHeight: isMobile ? "70px" : "100px",
          }}
          data-testid="homepage-banner"
        >
        {/* Rotating slides */}
        {banners.map((b, i) => {
          const isActive = i === index;
          // Inline styles for transitions — more reliable than Tailwind class
          // toggling and works regardless of JIT class detection.
          let slideStyle = { transition: "opacity 600ms ease, transform 600ms ease" };
          if (transition === "fade") {
            slideStyle.opacity = isActive ? 1 : 0;
          } else if (transition === "slide") {
            slideStyle.opacity = 1;
            slideStyle.transform = isActive
              ? "translateX(0)"
              : i < index ? "translateX(-100%)" : "translateX(100%)";
          } else {
            // "none"
            slideStyle = { opacity: isActive ? 1 : 0 };
          }
          return (
            <button
              key={b.banner_id}
              onClick={() => isActive && handleClick(b)}
              className={`absolute inset-0 w-full h-full text-left ${
                isActive ? "cursor-pointer pointer-events-auto" : "pointer-events-none"
              }`}
              style={slideStyle}
              tabIndex={isActive ? 0 : -1}
              aria-hidden={!isActive}
              aria-label={b.alt_text || "Banner"}
              data-testid={`banner-slide-${b.banner_id}`}
            >
              <BannerMedia
                banner={b}
                isMobile={isMobile}
                onEnded={
                  isActive &&
                  rotationMode === "media_aware" &&
                  b.media_type === "video" &&
                  banners.length > 1
                    ? advance
                    : undefined
                }
                key={
                  b.media_type === "video" || b.media_type === "gif"
                    ? `${b.banner_id}-${activations[b.banner_id] || 0}`
                    : b.banner_id
                }
              />
            </button>
          );
        })}

        {/* Label badge */}
        {current.label && (
          <div
            className="absolute top-2 left-2 bg-black/50 text-white text-[10px] uppercase tracking-wide px-2 py-0.5 rounded backdrop-blur-sm pointer-events-none"
            data-testid="banner-label"
          >
            {current.label}
          </div>
        )}

        {/* Pagination dots */}
        {banners.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none">
            {banners.map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full ${
                  i === index ? "bg-white" : "bg-white/40"
                }`}
              />
            ))}
          </div>
        )}

        {/* Collapse button */}
        <button
          onClick={handleCollapse}
          className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 backdrop-blur-sm transition-colors"
          aria-label="Collapse banner"
          data-testid="banner-collapse-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        </div>
      </div>

      {/* Article drawer */}
      <BannerArticleDrawer
        banner={activeArticleBanner}
        open={articleOpen}
        onClose={() => setArticleOpen(false)}
      />

      {/* PDF drawer */}
      <BannerPdfDrawer
        pdfUrl={activePdfBanner?.click_pdf_url}
        title={activePdfBanner?.alt_text}
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
      />
    </>
  );
}

export default HomepageBanner;
